import { useState, useEffect } from 'react';
import { Building2, Search, Plus, Lock, Crown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useNavigate } from 'react-router-dom';
import CreateListingForm from '../components/offseason/CreateListingForm';
import SearchListings from '../components/offseason/SearchListings';
import MyListings from '../components/offseason/MyListings';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DashboardPage({ currentUser }) {
  const [activeTab, setActiveTab] = useState('my-listings');
  const navigate = useNavigate();

  // Check if user is Premium
  const isPremium = currentUser?.subscriptionPlan === 'Premium';

  // If not premium, show locked state
  if (!isPremium) {
    return (
      <div className="min-h-screen pt-20 bg-gray-50 px-4 py-12">
        <div className="max-w-5xl mx-auto">
          <Card className="border-2 border-gray-200">
            <CardContent className="py-16 text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="h-10 w-10 text-gray-400" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Off-Season Collaboration Network
              </h2>
              <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
                Upgrade to Premium to access the Off-Season Collaboration Network and connect with other resorts.
              </p>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-6 mb-8 max-w-2xl mx-auto">
                <h3 className="font-semibold text-emerald-900 mb-3">Premium Features Include:</h3>
                <ul className="text-left space-y-2 text-gray-700">
                  <li className="flex items-center space-x-2">
                    <Crown className="h-5 w-5 text-emerald-600" />
                    <span>List your off-season availability</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Crown className="h-5 w-5 text-emerald-600" />
                    <span>Search and view other resort listings</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Crown className="h-5 w-5 text-emerald-600" />
                    <span>Connect directly via Email & WhatsApp</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <Crown className="h-5 w-5 text-emerald-600" />
                    <span>Negotiate terms directly - no middleman</span>
                  </li>
                </ul>
              </div>
              <Button
                size="lg"
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate('/pricing')}
              >
                <Crown className="h-5 w-5 mr-2" />
                Upgrade to Premium
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Off-Season Collaboration Network</h1>
              <p className="text-gray-600">
                Publish your off-season availability and connect with other resorts. Negotiate terms directly — Travelodges does not handle payments.
              </p>
            </div>
            <Badge className="bg-gradient-to-r from-amber-500 to-amber-600 text-white px-4 py-2 text-sm">
              <Crown className="h-4 w-4 mr-2 inline" />
              Premium Member
            </Badge>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex space-x-8">
            <button
              onClick={() => setActiveTab('my-listings')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'my-listings'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Building2 className="h-5 w-5 inline mr-2" />
              My Listings
            </button>
            <button
              onClick={() => setActiveTab('create')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'create'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Plus className="h-5 w-5 inline mr-2" />
              Create Listing
            </button>
            <button
              onClick={() => setActiveTab('search')}
              className={`py-4 px-2 border-b-2 font-medium text-sm ${
                activeTab === 'search'
                  ? 'border-emerald-600 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <Search className="h-5 w-5 inline mr-2" />
              Search Network
            </button>
          </div>
        </div>
      </div>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'my-listings' && <MyListings currentUser={currentUser} />}
        {activeTab === 'create' && <CreateListingForm currentUser={currentUser} onSuccess={() => setActiveTab('my-listings')} />}
        {activeTab === 'search' && <SearchListings currentUser={currentUser} />}
      </div>
    </div>
  );
}

export default DashboardPage;
